(() => {
  class LetoileOtpAuth {
    constructor(root = document) {
      this.forms = [...root.querySelectorAll('[data-otp-auth]')].filter((form) => !form.dataset.otpAuthBound);
      this.forms.forEach((form) => {
        form.dataset.otpAuthBound = 'true';
        this.bindForm(form);
      });
    }

    bindForm(form) {
      const state = { flow: '', oidcLoginHint: '', phone: '', redirecting: false, requestId: '' };
      const phoneInput = form.querySelector('[data-otp-phone]');
      const codeInput = form.querySelector('[data-otp-code]');
      const sendButton = form.querySelector('[data-otp-send]');
      const verifyButton = form.querySelector('[data-otp-verify]');
      const resendButton = form.querySelector('[data-otp-resend]');
      const finishButton = form.querySelector('[data-otp-finish]');

      phoneInput?.addEventListener('input', () => {
        phoneInput.value = this.formatPhoneInput(form, phoneInput.value);
      });
      phoneInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          sendButton?.click();
        }
      });
      codeInput?.addEventListener('input', () => {
        codeInput.value = this.digits(codeInput.value).slice(0, this.codeDigits(form));
        this.updateVerifyButton(form, codeInput, verifyButton);
      });
      codeInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          if (!verifyButton?.disabled) verifyButton.click();
        }
      });
      sendButton?.addEventListener('click', () => this.sendCode(form, state, phoneInput, sendButton));
      resendButton?.addEventListener('click', () => this.requestCode(form, state, resendButton));
      verifyButton?.addEventListener('click', () => this.verifyCode(form, state, codeInput, verifyButton));
      finishButton?.addEventListener('click', () => this.finishLogin(form, state, finishButton));
    }

    async sendCode(form, state, phoneInput, button) {
      const local = this.extractLocalDigits(form, phoneInput?.value || '');
      this.clearError(form);

      if (!this.isValidLocalPhone(form, local)) {
        this.showError(form, form.dataset.otpPhoneError);
        return;
      }

      state.phone = this.countryCode(form) + local;
      await this.requestCode(form, state, button);
    }

    async requestCode(form, state, button) {
      if (!state.phone || state.redirecting) return;

      this.setLoading(button, true);
      this.clearError(form);
      try {
        const result = await this.requestJson(form, '/auth/otp/request', {
          market: form.dataset.otpMarket || 'QA',
          phone: state.phone,
          purpose: 'login',
        });

        state.requestId = result.requestId || '';
        state.flow = result.flow || 'existing';
        state.oidcLoginHint = '';
        form.querySelector('[data-otp-readable-phone]').textContent = this.formatReadablePhone(form, state.phone);
        form.querySelector('[data-otp-code]').value = '';
        this.updateVerifyButton(form, form.querySelector('[data-otp-code]'), form.querySelector('[data-otp-verify]'));
        form.querySelector('[data-otp-terms-row]').hidden = state.flow !== 'registration';
        form.querySelector('[data-otp-marketing-row]').hidden = state.flow !== 'registration';
        form.querySelector('[data-otp-mock]').hidden = !result.mocked;
        this.setStep(form, 'code');
        form.querySelector('[data-otp-code]')?.focus();
      } catch (error) {
        this.showError(form, this.errorMessage(error, form.dataset.otpRequestError, form.dataset.otpNetworkError));
      } finally {
        this.setLoading(button, false);
      }
    }

    async verifyCode(form, state, codeInput, button) {
      if (state.redirecting) return;

      const code = this.digits(codeInput?.value || '').slice(0, this.codeDigits(form));
      this.clearError(form);

      if (code.length < this.codeDigits(form)) {
        this.showError(form, form.dataset.otpIncompleteError);
        return;
      }

      if (state.flow === 'registration' && !form.querySelector('[data-otp-terms]')?.checked) {
        this.showError(form, form.dataset.otpTermsError);
        return;
      }

      this.setLoading(button, true);
      try {
        const result = await this.requestJson(form, '/auth/otp/verify', {
          requestId: state.requestId,
          code,
          phone: state.phone,
          termsAccepted: Boolean(form.querySelector('[data-otp-terms]')?.checked),
          marketingAccepted: Boolean(form.querySelector('[data-otp-marketing]')?.checked),
        });

        state.oidcLoginHint = result.oidcLoginHint || '';
        if (!state.oidcLoginHint) throw new Error(form.dataset.otpVerifyError);

        state.redirecting = true;
        this.setButtonText(button, form.dataset.otpRedirectingText);
        this.redirectToLogin(form, state.oidcLoginHint);
      } catch (error) {
        state.redirecting = false;
        this.resetButtonText(button);
        this.showError(form, this.errorMessage(error, form.dataset.otpVerifyError, form.dataset.otpNetworkError));
      } finally {
        if (!state.redirecting) {
          this.setLoading(button, false);
          this.updateVerifyButton(form, codeInput, button);
        }
      }
    }

    finishLogin(form, state, button) {
      if (state.redirecting) return;

      this.clearError(form);
      this.setLoading(button, true);

      if (!state.oidcLoginHint) {
        this.setLoading(button, false);
        this.showError(form, form.dataset.otpVerifyError);
        return;
      }

      state.redirecting = true;
      this.redirectToLogin(form, state.oidcLoginHint);
    }

    redirectToLogin(form, loginHint) {
      const loginUrl = new URL(form.dataset.otpLoginUrl || '/customer_authentication/login', window.location.origin);
      const currentParams = new URLSearchParams(window.location.search);

      currentParams.forEach((value, key) => {
        if (key === 'login_hint') return;
        loginUrl.searchParams.set(key, value);
      });

      loginUrl.searchParams.set('return_to', this.returnToPath(form));
      loginUrl.searchParams.set('login_hint', loginHint);
      window.location.assign(loginUrl.toString());
    }

    returnToPath(form) {
      const explicit = new URLSearchParams(window.location.search).get('return_to');
      if (this.isAllowedReturnTo(explicit)) return explicit;

      const configured = form.dataset.otpReturnTo;
      if (configured !== '/' && this.isAllowedReturnTo(configured)) return configured;

      return '/';
    }

    isRelativePath(value) {
      return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');
    }

    isAllowedReturnTo(value) {
      if (this.isRelativePath(value)) return true;

      try {
        const url = new URL(value);
        return url.protocol === 'https:' && (
          url.origin === window.location.origin ||
          url.hostname === 'shopify.com' ||
          url.hostname.endsWith('.shopify.com')
        );
      } catch (_error) {
        return false;
      }
    }

    async requestJson(form, path, body) {
      const baseUrl = String(form.dataset.otpServiceUrl || '').replace(/\/$/, '');
      if (!baseUrl) throw new Error(form.dataset.otpNetworkError);

      let response;
      try {
        response = await fetch(baseUrl + path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } catch (_error) {
        throw new Error(form.dataset.otpNetworkError);
      }

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error?.message || 'OTP request failed with HTTP ' + response.status + '.');
      return payload;
    }

    setStep(form, step) {
      form.querySelectorAll('[data-otp-step]').forEach((element) => {
        element.hidden = element.getAttribute('data-otp-step') !== step;
      });
    }

    showError(form, message) {
      const visibleStep = form.querySelector('[data-otp-step]:not([hidden])');
      const error = visibleStep?.querySelector('[data-otp-error]');
      if (!error) return;

      error.textContent = message || form.dataset.otpVerifyError || '';
      error.hidden = false;
    }

    clearError(form) {
      form.querySelectorAll('[data-otp-error]').forEach((error) => {
        error.textContent = '';
        error.hidden = true;
      });
    }

    setLoading(button, loading) {
      if (!button) return;
      button.disabled = loading;
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    setButtonText(button, text) {
      const textNode = button?.querySelector('[data-otp-default-text]');
      if (!textNode || !text) return;
      textNode.textContent = text;
    }

    resetButtonText(button) {
      const textNode = button?.querySelector('[data-otp-default-text]');
      if (!textNode) return;
      textNode.textContent = textNode.dataset.otpDefaultText || textNode.textContent;
    }

    updateVerifyButton(form, codeInput, button) {
      if (!button) return;
      button.disabled = this.digits(codeInput?.value || '').length < this.codeDigits(form);
    }

    errorMessage(error, fallback, networkFallback) {
      if (!(error instanceof Error)) return fallback;
      if (error.message === 'Failed to fetch' || error.message === 'Network request failed') return networkFallback;
      return error.message || fallback;
    }

    codeDigits(form) {
      const digits = Number.parseInt(form.dataset.otpCodeDigits || '4', 10);
      return Number.isFinite(digits) ? Math.min(Math.max(digits, 4), 8) : 4;
    }

    digits(value) {
      return String(value || '').replace(/\D/g, '');
    }

    countryCode(form) {
      return form.dataset.otpCountryCode || '+971';
    }

    countryCodeDigits(form) {
      return this.digits(this.countryCode(form));
    }

    localDigitsCount(form) {
      const count = Number.parseInt(form.dataset.otpLocalDigits || '9', 10);
      return Number.isFinite(count) ? Math.min(Math.max(count, 6), 12) : 9;
    }

    phoneGroups(form) {
      return String(form.dataset.otpPhoneGroups || '2,3,4')
        .split(',')
        .map((value) => Number.parseInt(value, 10))
        .filter((value) => Number.isFinite(value) && value > 0);
    }

    phonePrefixes(form) {
      return String(form.dataset.otpPhonePrefixes || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
    }

    extractLocalDigits(form, value) {
      const digits = this.digits(value);
      const country = this.countryCodeDigits(form);
      const rawLocal = country && digits.startsWith(country) ? digits.slice(country.length) : digits;
      const localDigits = this.localDigitsCount(form);
      const local = rawLocal.startsWith('0') && rawLocal.length > localDigits ? rawLocal.slice(1) : rawLocal;
      return local.slice(0, localDigits);
    }

    clampLocalDigits(form, value) {
      const local = this.extractLocalDigits(form, value);
      const prefixes = this.phonePrefixes(form);
      if (!local) return '';
      if (prefixes.length === 0) return local;
      const canMatchPrefix = prefixes.some((prefix) => prefix.startsWith(local) || local.startsWith(prefix));
      if (!canMatchPrefix) return local.slice(0, -1);
      return local;
    }

    isValidLocalPhone(form, local) {
      const prefixes = this.phonePrefixes(form);
      return local.length === this.localDigitsCount(form) && (
        prefixes.length === 0 || prefixes.some((prefix) => local.startsWith(prefix))
      );
    }

    formatPhoneInput(form, value) {
      const local = this.clampLocalDigits(form, value);
      const groups = this.phoneGroups(form);
      let index = 0;
      const parts = groups.map((size) => {
        const part = local.slice(index, index + size);
        index += size;
        return part;
      });
      if (index < local.length) parts.push(local.slice(index));
      return parts.filter(Boolean).join(' ');
    }

    formatReadablePhone(form, value) {
      return [this.countryCode(form), this.formatPhoneInput(form, value)].filter(Boolean).join(' ');
    }
  }

  document.addEventListener('DOMContentLoaded', () => new LetoileOtpAuth());
  document.addEventListener('shopify:section:load', (event) => new LetoileOtpAuth(event.target));
})();
